import type { ApiFetchResponse } from "@tmi/types/api/fetch";
import type { CourseApiResponse, Courses } from "@tmi/types/api/content-models";
import type { Course, Lesson, Product, Question, Section } from "@tmi/types/entities";
import { cloneDeep } from "lodash-es";
import { isFreelyAvailableContent } from "@/utils/access-utils";
import { useProductsStore } from "@/stores/Products";
import pinia from "@/plugins/PiniaPlugin";
import { usePermissionsStore } from "@/stores/Permissions";

// import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";
// const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export function mapCourseApiResponseToCourseCatalog( courses: CourseApiResponse[] ): Course[] {
    const permissionsStore = usePermissionsStore( pinia );
    const productsStore = useProductsStore( pinia );

    const loadedCourses = [ ...( courses
            .map( ( c: CourseApiResponse ): Course => ( {
                id: c.id,
                slug: c.slug,
                title: c.title,
                body: c.body,
                thumbnail_uri: c.thumbnail_uri,
                assoc_product_ids: c.assoc_product_ids,
                published_dt: c.published_dt,
                sections: !c.sections ? [] : c.sections.map( ( s: Section ) => ( {
                    id: s.id,
                    title: s.title,
                    sequence_number: s.sequence_number,
                    lessons: s.lessons.map( ( l: Lesson ) => ( {
                        id: l.id,
                        title: l.title,
                        slug: l.slug,
                        sequence_number: l.sequence_number,
                        vimeo_video_id: l.vimeo_video_id,
                        questions: !l.questions ? null : l.questions?.map( ( q: Question ) => ( {
                            id: q.id,
                            question: q.question,
                            correct_answer_number: q.correct_answer_number,
                            answers: [ ...q.answers ],
                            clarification: q.clarification
                        } ) )
                    } ) )
                } ) )
            } ) )
    ) ];

    return [ ...loadedCourses.filter( ( c: Course ) => {
        const products: Product[] = [];
        c.assoc_product_ids?.forEach( ( id: string ) => {
            const product: Product | undefined = productsStore.productById( id );

            if ( product ) {
                products.push( cloneDeep( product ) );
            }
        } );

        return products.some( ( p: Product ) =>
            !!( p.codes ) && ( permissionsStore.hasAccessBasedOnShortCode( p.codes ) || isFreelyAvailableContent( p.slug ) )
        )
    } ) ];
}

/*
export async function refreshFromServer( authToken: string ): Promise< Course[] > {
    let allCourses = [] as Course[];
    try {
        // Timing - testing to see how expensive each method is
        const t0: number = performance.now();

        const response =
            await goFetch< CourseApiResponse, PersistedQueryPayload >( {
                endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                payload: {
                    id: '76d0e573f960a44a6e8d5a08728c33d7',
                    variables: { skip: 0, limit: 10 }
                },
                authToken: authToken
            } );

        if ( null != response?.data?.coursesCatalog ) {
            allCourses = mapCourseApiResponseToCourseCatalog( response );

            const t1: number = performance.now();
            logger.info( `Fetching & mapping ${ allCourses.length } course items took ${ t1 - t0 } ms` );
        }
    } catch ( err: any ) {
        logger.error( JSON.stringify( err.message ) );
    }

    return allCourses;
}
*/
